import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Feedback } from '../components/Feedback';
import { formatCurrency } from '../lib/format';
import { PLATFORM_LABEL } from '../lib/platforms';
import {
  ApiError,
  clearViewerToken,
  decodeViewerIdentity,
  fetchCreator,
  fetchViewerProviders,
  getViewerToken,
  saveViewerToken,
  sendMessage,
  viewerAuthStartUrl,
  type PublicCreator,
  type ViewerIdentity,
  type ViewerProvider,
} from '../services/api';

/** Valores sugeridos (em centavos) para envio rápido. */
const QUICK_AMOUNTS = [200, 500, 1000, 2500];

/**
 * Página PÚBLICA do criador (ex.: /maria): é aqui que o espectador
 * escreve a mensagem e escolhe o valor.
 *
 * Identificação do espectador — 3 fluxos:
 *   1. Não digita nada → o servidor gera um nome aleatório divertido
 *   2. Digita o nome que quiser (sem login)
 *   3. Entra com a conta da plataforma da live (Twitch/YouTube/Kick) e o
 *      nome verificado vem de lá (com selo "via ..." para o criador)
 *
 * O pagamento em si será feito pelo gateway externo em uma fase futura.
 */
export function SendMessagePage() {
  const { username = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [creator, setCreator] = useState<PublicCreator | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Identificação (fluxo 3): providers disponíveis + sessão do espectador
  const [providers, setProviders] = useState<ViewerProvider[]>([]);
  const [viewer, setViewer] = useState<ViewerIdentity | null>(null);
  const [viewerError, setViewerError] = useState(false);

  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [amountReais, setAmountReais] = useState('5,00');
  const [status, setStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'sending' }
    | { kind: 'success'; sentAs: string }
    | { kind: 'error'; text: string }
  >({ kind: 'idle' });

  // Captura o retorno do login social (?viewer=token | ?viewer_error=1)
  useEffect(() => {
    const token = searchParams.get('viewer');
    const hadError = searchParams.get('viewer_error');

    if (token) {
      saveViewerToken(token);
    }
    if (hadError) {
      setViewerError(true);
    }
    if (token || hadError) {
      // Limpa a URL (o token não deve ficar visível/na história do navegador)
      setSearchParams({}, { replace: true });
    }

    const stored = getViewerToken();
    if (stored) {
      const identity = decodeViewerIdentity(stored);
      if (identity) setViewer(identity);
      else clearViewerToken(); // expirado/corrompido
    }
  }, [searchParams, setSearchParams]);

  // Carrega os dados públicos do criador e os provedores de login
  useEffect(() => {
    let cancelled = false;

    fetchCreator(username)
      .then((data) => {
        if (!cancelled) setCreator(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(
            err instanceof ApiError && err.status === 404
              ? 'Criador não encontrado. Confira o link.'
              : 'Não foi possível carregar a página. Tente novamente.',
          );
        }
      });

    fetchViewerProviders()
      .then((data) => {
        if (!cancelled) setProviders(data.providers);
      })
      .catch(() => {
        // Sem login social configurado — os fluxos 1 e 2 continuam valendo
      });

    return () => {
      cancelled = true;
    };
  }, [username]);

  function logoutViewer() {
    clearViewerToken();
    setViewer(null);
  }

  /** Converte "12,34" (ou "12.34") para centavos; null se inválido. */
  function parseAmountCents(value: string): number | null {
    const normalized = Number(value.replace(/\./g, '').replace(',', '.'));
    if (Number.isNaN(normalized)) return null;
    return Math.round(normalized * 100);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const amountCents = parseAmountCents(amountReais);
    if (amountCents === null || amountCents < 100) {
      setStatus({ kind: 'error', text: 'Informe um valor válido (mínimo R$ 1,00).' });
      return;
    }

    setStatus({ kind: 'sending' });
    try {
      const sent = await sendMessage(username, {
        message,
        amountCents,
        // Fluxo 3: token verificado; fluxos 1/2: nome digitado (ou vazio)
        ...(viewer ? { viewerToken: getViewerToken() ?? undefined } : { senderName }),
      });
      setStatus({ kind: 'success', sentAs: sent.senderName });
      setMessage('');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'VIEWER_TOKEN_INVALID') {
        logoutViewer();
        setStatus({
          kind: 'error',
          text: 'Sua identificação expirou. Entre novamente ou envie sem login.',
        });
        return;
      }
      if (err instanceof ApiError && err.issues?.length) {
        setStatus({ kind: 'error', text: err.issues[0].error });
      } else {
        setStatus({
          kind: 'error',
          text: err instanceof Error ? err.message : 'Erro inesperado ao enviar.',
        });
      }
    }
  }

  if (loadError) {
    return (
      <main className="auth-page">
        <div className="card">
          <p>{loadError}</p>
          <p>
            <Link to="/">Voltar ao início</Link>
          </p>
        </div>
      </main>
    );
  }

  if (!creator) {
    return <p className="page-loading">Carregando…</p>;
  }

  return (
    <main className="auth-page">
      <form className="card form" onSubmit={handleSubmit}>
        <h1 className="form__title">Enviar mensagem para {creator.name}</h1>
        <p className="form__help">
          Sua mensagem aparece para o criador durante a live. Valor mínimo: R$ 1,00.
        </p>

        {/* ---- Identificação do espectador ---- */}
        {viewer ? (
          <div className="viewer-identity">
            <p className="viewer-identity__text">
              Enviando como <strong>{viewer.name}</strong>
              <span className="viewer-identity__badge">via {PLATFORM_LABEL[viewer.platform]}</span>
            </p>
            <button
              className="button button--small button--ghost"
              type="button"
              onClick={logoutViewer}
            >
              Sair
            </button>
          </div>
        ) : (
          <div className="form__field">
            <label className="form__label" htmlFor="senderName">
              Seu nome <span className="form__optional">(opcional)</span>
            </label>
            <input
              className="form__input"
              id="senderName"
              name="senderName"
              type="text"
              autoComplete="nickname"
              maxLength={100}
              placeholder="Deixe vazio para enviar com um nome aleatório"
              value={senderName}
              onChange={(event) => setSenderName(event.target.value)}
            />
            {providers.length > 0 && (
              <div className="viewer-login">
                <span className="viewer-login__hint">ou entre com a conta de onde assiste:</span>
                <div className="viewer-login__buttons">
                  {providers.map((provider) => (
                    <a
                      key={provider.id}
                      className="button button--small button--ghost"
                      href={viewerAuthStartUrl(provider.id, username)}
                    >
                      Entrar com {provider.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
            {viewerError && (
              <Feedback kind="error">
                Não foi possível entrar com a plataforma. Você pode digitar um nome ou enviar
                anônimo.
              </Feedback>
            )}
          </div>
        )}

        <div className="form__field">
          <label className="form__label" htmlFor="message">
            Mensagem
          </label>
          <textarea
            className="form__textarea"
            id="message"
            required
            maxLength={500}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
        </div>

        <div className="form__field">
          <label className="form__label" htmlFor="amount">
            Valor (R$)
          </label>
          {/* Atalhos de valor: um toque preenche o campo (bom para mobile) */}
          <div className="quick-amounts">
            {QUICK_AMOUNTS.map((cents) => (
              <button
                key={cents}
                type="button"
                className={`quick-amounts__option${
                  parseAmountCents(amountReais) === cents ? ' quick-amounts__option--active' : ''
                }`}
                onClick={() => setAmountReais((cents / 100).toFixed(2).replace('.', ','))}
              >
                {formatCurrency(cents)}
              </button>
            ))}
          </div>
          <input
            className="form__input"
            id="amount"
            type="text"
            inputMode="decimal"
            required
            value={amountReais}
            onChange={(event) => setAmountReais(event.target.value)}
          />
        </div>

        {status.kind === 'success' && (
          <Feedback kind="success">
            {`Mensagem enviada como ${status.sentAs}! Obrigado pelo apoio.`}
          </Feedback>
        )}
        {status.kind === 'error' && <Feedback kind="error">{status.text}</Feedback>}

        <button
          className="button button--primary"
          type="submit"
          disabled={status.kind === 'sending'}
        >
          {status.kind === 'sending' ? 'Enviando…' : 'Enviar mensagem'}
        </button>
      </form>
    </main>
  );
}
